import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import {
  FiX,
  FiPlus,
  FiTrash2,
  FiCalendar,
  FiRepeat,
  FiMail,
  FiShield,
  FiSave,
  FiLoader
} from 'react-icons/fi';
import { RECURRING_FREQUENCIES } from '../../data/recurringConstants';
import { calculateInvoiceTotals } from '../../utils/invoiceLogic';
import { calculateNextOccurrence } from '../../utils/recurringLogic';
import { invoiceService } from '../../services/invoiceService';
import { recurringService } from '../../services/recurringService';
import { useClient } from '../../context/ClientContext';

export default function RecurringInvoiceForm({ isOpen, onClose, initialData = null, onSuccess }) {
  const { activeClient } = useClient();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    auto_send: true,
    send_reminders: true,
    status: 'Active',
    items: [{ id: 1, product: '', description: '', qty: 1, price: 0 }]
  });

  useEffect(() => {
    if (isOpen && activeClient?.id) {
      loadDependencies();
    }
  }, [isOpen, initialData, activeClient?.id]);

  const loadDependencies = async () => {
    setIsLoading(true);
    try {
      const customersData = await invoiceService.getCustomers(activeClient.id);
      setCustomers(customersData || []);

      if (initialData) {
        const fullData = await recurringService.getScheduleById(initialData.id);
        setFormData({
          id: fullData.id,
          customer_id: fullData.customer_id,
          frequency: fullData.frequency,
          start_date: fullData.start_date,
          end_date: fullData.end_date || '',
          auto_send: fullData.auto_send,
          send_reminders: fullData.send_reminders,
          status: fullData.status,
          items: fullData.items?.length > 0
            ? fullData.items.map((item) => ({
              id: item.id,
              product: item.product,
              description: item.description,
              qty: item.qty,
              price: item.price
            }))
            : [{ id: Date.now(), product: '', description: '', qty: 1, price: 0 }]
        });
      }
    } catch (error) {
      console.error('Failed to load dependencies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totals = calculateInvoiceTotals(formData.items, 0);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { id: Date.now(), product: '', description: '', qty: 1, price: 0 }]
    });
  };

  const removeItem = (id) => {
    if (formData.items.length > 1) {
      setFormData({ ...formData, items: formData.items.filter((item) => item.id !== id) });
    }
  };

  const updateItem = (id, field, value) => {
    const items = formData.items.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, items });
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!formData.customer_id) {
      alert('Please select a customer.');
      return;
    }

    setIsSaving(true);

    try {
      const nextDate = calculateNextOccurrence(formData.start_date, formData.frequency);
      const payload = {
        customer_id: formData.customer_id,
        frequency: formData.frequency,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        auto_send: formData.auto_send,
        send_reminders: formData.send_reminders,
        amount: totals.total,
        status: formData.status,
        next_date: nextDate
      };

      if (initialData?.id) {
        await recurringService.updateSchedule(initialData.id, payload, formData.items);
      } else {
        await recurringService.createSchedule(payload, formData.items, activeClient.id);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save schedule:', error);
      alert(`Failed to save schedule. ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
      <div className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <SafeIcon icon={FiRepeat} className="text-xl" />
            </div>
            <div>
              <h2 className="text-[17px] font-semibold text-slate-900">
                {initialData ? 'Edit Recurring Schedule' : 'Create Recurring Schedule'}
              </h2>
              <p className="mt-0.5 text-[11px] font-normal text-slate-500">Automation template</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            title="Close"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-emerald-500" />
          </div>
        ) : (
          <form id="recurring-form" onSubmit={handleSave} className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <div className="space-y-5 lg:col-span-4">
                <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.1em] text-slate-700">
                      <SafeIcon icon={FiCalendar} className="text-sm text-blue-500" />
                      Schedule Details
                    </h3>

                    <div className="space-y-1.5">
                      <label className="text-[11px] font-medium text-slate-500">Frequency</label>
                      <select
                        value={formData.frequency}
                        onChange={(event) => setFormData({ ...formData, frequency: event.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[13px] font-normal text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/10"
                      >
                        {RECURRING_FREQUENCIES.map((frequency) => (
                          <option key={frequency.id} value={frequency.id}>
                            {frequency.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-medium text-slate-500">Start date</label>
                        <input
                          type="date"
                          required
                          value={formData.start_date}
                          onChange={(event) => setFormData({ ...formData, start_date: event.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-[12px] font-normal text-slate-700 outline-none focus:border-emerald-400"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[11px] font-medium text-slate-500">End date</label>
                        <input
                          type="date"
                          value={formData.end_date}
                          onChange={(event) => setFormData({ ...formData, end_date: event.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-[12px] font-normal text-slate-700 outline-none focus:border-emerald-400"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-slate-100 pt-5">
                    <h3 className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.1em] text-slate-700">
                      <SafeIcon icon={FiMail} className="text-sm text-emerald-500" />
                      Delivery Settings
                    </h3>

                    <label className="flex cursor-pointer items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5 transition-colors hover:bg-slate-100">
                      <span className="text-[12px] font-normal text-slate-700">Auto-send via email</span>
                      <input
                        type="checkbox"
                        checked={formData.auto_send}
                        onChange={(event) => setFormData({ ...formData, auto_send: event.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </label>

                    <label className="flex cursor-pointer items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5 transition-colors hover:bg-slate-100">
                      <span className="text-[12px] font-normal text-slate-700">Payment reminders</span>
                      <input
                        type="checkbox"
                        checked={formData.send_reminders}
                        onChange={(event) => setFormData({ ...formData, send_reminders: event.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </label>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-2xl bg-emerald-600 p-5 text-white shadow-sm">
                  <div className="relative z-10">
                    <h4 className="text-[14px] font-medium">Pro tip</h4>
                    <p className="mt-1.5 text-[12px] font-normal leading-relaxed text-emerald-100">
                      Monthly recurring billing helps create predictable cash flow.
                    </p>
                  </div>
                  <SafeIcon icon={FiShield} className="absolute -bottom-3 -right-3 text-6xl text-white/10" />
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                    <h3 className="text-[12px] font-medium uppercase tracking-[0.1em] text-slate-700">
                      Invoice Template
                    </h3>

                    <select
                      required
                      value={formData.customer_id}
                      onChange={(event) => setFormData({ ...formData, customer_id: event.target.value })}
                      className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] font-normal text-slate-700 outline-none focus:border-emerald-400"
                    >
                      <option value="">Select customer...</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-3">
                    {formData.items.map((item) => (
                      <div
                        key={item.id}
                        className="grid grid-cols-12 items-end gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                      >
                        <div className="col-span-12 space-y-1.5 sm:col-span-6">
                          <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                            Service description
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="Monthly subscription..."
                            value={item.product}
                            onChange={(event) => updateItem(item.id, 'product', event.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-normal text-slate-700 outline-none focus:border-emerald-400"
                          />
                        </div>

                        <div className="col-span-4 space-y-1.5 sm:col-span-2">
                          <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                            Qty
                          </label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            required
                            value={item.qty}
                            onChange={(event) => updateItem(item.id, 'qty', parseFloat(event.target.value) || 0)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-center text-[13px] font-normal text-slate-700 outline-none focus:border-emerald-400"
                          />
                        </div>

                        <div className="col-span-6 space-y-1.5 sm:col-span-3">
                          <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                            Unit price
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            placeholder="0.00"
                            value={item.price}
                            onChange={(event) => updateItem(item.id, 'price', parseFloat(event.target.value) || 0)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-right text-[13px] font-normal text-slate-700 outline-none focus:border-emerald-400"
                          />
                        </div>

                        <div className="col-span-2 flex justify-end sm:col-span-1">
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="rounded-lg p-2 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"
                            title="Remove line item"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addItem}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-2.5 text-[12px] font-medium text-slate-500 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                    >
                      <SafeIcon icon={FiPlus} />
                      Add Line Item
                    </button>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <div className="w-full max-w-[250px] space-y-2.5">
                      <div className="flex justify-between text-[13px] font-normal text-slate-500">
                        <span>Subtotal</span>
                        <span>${totals.subtotal}</span>
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                        <span className="text-[14px] font-medium text-slate-800">Total per cycle</span>
                        <span className="text-[19px] font-semibold text-emerald-600">${totals.total}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2.5 text-[13px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="recurring-form"
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 rounded-xl bg-[#22c55e] px-5 py-2.5 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} />
            {isSaving ? 'Saving...' : initialData ? 'Update Schedule' : 'Start Recurring Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}